import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "@/api/client";
import type { components } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import {
  BOARDS, FIT_LABEL, KIND_LABEL, ROLE_LABEL, SETTING_FIELDS, STATUS_LABEL, WORK_KIND,
  SEVERITY_CLASS, SEVERITY_LABEL, TOUCHES_TBD, n, peopleWords, roleFilters, scoreRowWords, tabs, tierIndex, weekLine, weekWords,
  type Board, type Role, type Status, type Tab,
} from "@/features/ideas/hqModel";

export const Route = createFileRoute("/_authed/ideas/")({
  component: IdeasHQ,
});

type HqBoard = components["schemas"]["HqBoard"];
type HqTheme = components["schemas"]["HqTheme"];
type HqPick = components["schemas"]["HqPick"];
type HqWorkItem = components["schemas"]["HqWorkItem"];
type HqWorkItems = components["schemas"]["HqWorkItems"];
type HqScore = components["schemas"]["HqScore"];
type HqScoreRow = components["schemas"]["HqScoreRow"];
type HqSettings = components["schemas"]["HqSettings"];
type HqProblem = components["schemas"]["HqProblem"];
type HqAlready = components["schemas"]["HqAlready"];
type HqHealth = components["schemas"]["HqHealth"];

/** Send was refused because the week on screen is no longer the week (sent or changed). */
class Stale extends Error {}

/** The API's error body is { error: { message } }; say its words, or a plain fallback. */
function errorMessage(error: unknown, fallback: string): string {
  const message = (error as { error?: { message?: string } } | null)?.error?.message;
  return message || fallback;
}

/**
 * Ideas from the Pack, HQ (Randy and Jeff only; the server's admin gate hides it from everyone
 * else). One queue per board: the weekly funnel, the 5 2oodles picked with why, Keep and Swap,
 * the bench (nothing is ever deleted), Send this week's 5 (nothing goes out until you send it),
 * and who said what, word for word.
 */
function IdeasHQ() {
  const [tab, setTab] = useState<Tab>("family");
  const board: Board = tab === "pro" ? "pro" : "family";
  const [open, setOpen] = useState<string | null>(null);
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["admin", "ideas", board],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/ideas/{board}", { params: { path: { board } } });
      if (error) throw new Error("failed to load ideas");
      return data!;
    },
  });
  // Phase E1: Ready to build (and the rhythm strip) for every tab, so its count shows.
  const work = useQuery({
    queryKey: ["admin", "ideas", "work"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/ideas/work");
      if (error) throw new Error("failed to load");
      return data!;
    },
  });
  // Phase E2: Problems and Already in 2oodles carry their counts on the tabs.
  const problems = useQuery({
    queryKey: ["admin", "ideas", "problems"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/ideas/problems");
      if (error) throw new Error("failed to load");
      return data!;
    },
  });
  const already = useQuery({
    queryKey: ["admin", "ideas", "already"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/ideas/already");
      if (error) throw new Error("failed to load");
      return data!;
    },
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin", "ideas"] });
  const b = query.data;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Ideas from the Pack</h1>
          <p className="text-sm text-zinc-500">The Pack suggests, 2oodles picks 5 a week, you keep or swap and send.</p>
        </div>
        <div role="tablist" className="flex gap-1 rounded-md border border-zinc-200 p-0.5 text-sm">
          {tabs(work.data?.items.length, problems.data?.open_count, already.data ? already.data.themes.length + already.data.matches.length : null).map(([key, label]) => (
            <button key={key} type="button" role="tab" aria-selected={tab === key} data-testid={`hq-tab-${key}`}
              onClick={() => { setTab(key); setOpen(null); }}
              className={`rounded px-3 py-1 ${tab === key ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      {work.data ? <Rhythm steps={work.data.rhythm} /> : null}
      {tab === "work" ? <ReadyToBuild work={work.data} loading={work.isLoading} onChanged={refresh} />
        : tab === "score" ? <NailedScore />
        : tab === "problems" ? <Problems list={problems.data?.problems} loading={problems.isLoading} onChanged={refresh} />
        : tab === "already" ? <Already data={already.data} loading={already.isLoading} onChanged={refresh} />
        : query.isLoading ? <p className="text-sm text-zinc-500">Loading…</p>
        : query.isError || !b ? (
          <Card><CardBody>
            <p role="alert" className="text-sm text-red-700">Couldn’t load the ideas. Your session may have expired.</p>
            <button type="button" className="mt-3 rounded border px-3 py-2 text-sm" onClick={() => void query.refetch()}>Retry</button>
          </CardBody></Card>
        ) : (
          <>
            <Funnel b={b} />
            <Week b={b} board={board} onOpen={setOpen} onChanged={refresh} />
            <Bench b={b} onOpen={setOpen} />
            {b.last_sent ? <LastSent b={b} onOpen={setOpen} /> : null}
            {open ? <Voices key={open} id={open} board={board} onClose={() => setOpen(null)} onChanged={refresh} /> : null}
          </>
        )}
    </div>
  );
}

function Funnel({ b }: { b: HqBoard }) {
  const f = b.funnel;
  return (
    <Card data-testid="hq-funnel"><CardBody>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
        <span><strong className="text-xl">{n(f.suggestions)}</strong> suggestions →</span>
        <span><strong className="text-xl">{n(f.themes)}</strong> themes →</span>
        <span><strong className="text-xl">{n(f.to_vote)}</strong> to vote on</span>
      </div>
      <p className="mt-2 text-sm text-zinc-600">
        <strong>{n(f.bugs)}</strong> problems sent to Jeff, never voted on
      </p>
    </CardBody></Card>
  );
}

function Counts({ t, board }: { t: HqTheme; board: Board }) {
  return (
    <span className="text-xs text-zinc-500">
      Asked {n(t.asked)}{board === "pro" ? ` · owners ${n(t.owners)} · team ${n(t.team)}` : ` · families ${n(t.families)}`}
      {t.matters ? ` · ${n(t.matters)} said it matters` : ""}
      {t.way_fit ? ` · ${FIT_LABEL[t.way_fit]}` : ""}
      {t.hq_note ? <span className="block text-zinc-600">HQ note: {t.hq_note}</span> : null}
    </span>
  );
}

function Week({ b, board, onOpen, onChanged }: { b: HqBoard; board: Board; onOpen: (id: string) => void; onChanged: () => void }) {
  const [swapping, setSwapping] = useState<number | null>(null);
  const [choice, setChoice] = useState("");
  const [confirming, setConfirming] = useState(false);
  const keep = useMutation({
    mutationFn: async (position: number) => {
      const { error } = await api.POST("/api/v1/admin/ideas/{board}/keep", { params: { path: { board } }, body: { position } });
      if (error) throw new Error("keep failed");
    },
    onSuccess: onChanged,
  });
  const swap = useMutation({
    mutationFn: async (v: { position: number; theme_id: string }) => {
      const { error } = await api.POST("/api/v1/admin/ideas/{board}/swap", { params: { path: { board } }, body: v });
      if (error) throw new Error(errorMessage(error, "Couldn’t swap that in."));
    },
    onSuccess: () => { setSwapping(null); setChoice(""); onChanged(); },
  });
  const send = useMutation({
    // Send exactly the week on screen: if it was sent, swapped or kept since, the server says so.
    mutationFn: async () => {
      const { data, error, response } = await api.POST("/api/v1/admin/ideas/{board}/send", {
        params: { path: { board } }, body: { week_of: b.week.week_of, revision: b.week.revision },
      });
      if (response.status === 409) throw new Stale(errorMessage(error, "This week changed or was already sent."));
      if (error) throw new Error("Couldn’t send. Nothing went out; try again.");
      return data!;
    },
    onSuccess: () => { setConfirming(false); onChanged(); },
  });
  const w = b.week;
  const swappable = b.bench.filter(t => t.status === "heard");
  const busy = keep.isPending || swap.isPending || send.isPending;
  return (
    <Card data-testid="hq-week"><CardBody>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-semibold">{BOARDS.find(x => x[0] === board)![1]} · {weekWords(w.week_of)}</h2>
        <span className="text-sm text-zinc-500" data-testid="hq-week-line">{weekLine(w.status, w.picks.length)}</span>
      </div>
      <ol className="mt-3 divide-y divide-zinc-100">
        {w.picks.map((p: HqPick) => (
          <li key={p.position} className="flex flex-col gap-2 py-3 md:flex-row md:items-start" data-testid={`hq-pick-${p.position}`}>
            <span className="w-6 shrink-0 text-sm font-semibold text-zinc-400">{p.position}</span>
            <div className="min-w-0 flex-1">
              <button type="button" className="text-left font-medium hover:underline" onClick={() => onOpen(p.theme.id)}>
                {p.theme.title}
              </button>
              {p.theme.sample ? <span className="ml-2 rounded bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-500">Sample</span> : null}
              <div><Counts t={p.theme} board={board} /></div>
              <p className="mt-1 text-sm text-zinc-700"><span className="text-zinc-500">Why 2oodles picked it:</span> {p.why}</p>
              {swapping === p.position ? (
                <div className="mt-2 flex flex-wrap items-center gap-2" role="group" aria-label={`Swap ${p.theme.title}`}>
                  <select aria-label="Theme to swap in" value={choice} onChange={e => setChoice(e.target.value)}
                    className="h-8 max-w-full rounded-md border border-zinc-300 px-2 text-sm">
                    <option value="">Choose a theme from the bench…</option>
                    {swappable.map(t => <option key={t.id} value={t.id}>{t.title} · asked {n(t.asked)}</option>)}
                  </select>
                  <Button size="sm" disabled={!choice || busy} onClick={() => swap.mutate({ position: p.position, theme_id: choice })}>Swap in</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setSwapping(null); setChoice(""); }}>Cancel</Button>
                </div>
              ) : null}
            </div>
            {w.status !== "sent" ? (
              <div className="flex shrink-0 gap-2">
                <Button size="sm" variant={p.kept ? "primary" : "secondary"} disabled={busy || p.kept}
                  data-testid={`hq-keep-${p.position}`} onClick={() => keep.mutate(p.position)}>
                  {p.kept ? "Kept" : "Keep"}
                </Button>
                <Button size="sm" variant="secondary" disabled={busy || !swappable.length} data-testid={`hq-swap-${p.position}`}
                  onClick={() => { setSwapping(p.position); setChoice(""); }}>Swap</Button>
              </div>
            ) : null}
          </li>
        ))}
      </ol>
      {!w.picks.length ? <p className="py-3 text-sm text-zinc-500">No ideas are waiting on this board yet.</p> : null}
      {swap.isError ? <p role="alert" className="text-sm text-red-700">{swap.error.message}</p> : null}
      {w.status !== "sent" && w.picks.length ? (
        <div className="mt-3 border-t border-zinc-100 pt-3">
          {confirming ? (
            <div role="group" aria-label="Confirm sending this week's 5" className="space-y-2 rounded-md border border-zinc-300 bg-zinc-50 p-3">
              <p className="text-sm font-medium">Send these {w.picks.length} to everyone on the {board === "pro" ? "Pro" : "Family"} board?</p>
              <p className="text-sm text-zinc-600">Everyone who asked for one of them gets a heads-up. This can’t be unsent.</p>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" disabled={send.isPending} onClick={() => setConfirming(false)}>Not yet</Button>
                <Button size="sm" disabled={send.isPending} data-testid="hq-send-confirm" onClick={() => send.mutate()}>
                  {send.isPending ? "Sending…" : "Send now"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <Button data-testid="hq-send" disabled={busy} onClick={() => setConfirming(true)}>Send this week’s {w.picks.length}</Button>
              <span className="text-sm text-zinc-500">{b.send_line}</span>
            </div>
          )}
          {send.isError ? (
            <div role="alert" className="mt-2 flex flex-wrap items-center gap-2 text-sm text-red-700" data-testid="hq-send-error">
              <span>{send.error.message}</span>
              {send.error instanceof Stale ? (
                <Button size="sm" variant="secondary" onClick={() => { setConfirming(false); send.reset(); onChanged(); }}>Reload</Button>
              ) : null}
            </div>
          ) : null}
          {send.isSuccess ? <p className="mt-2 text-sm text-zinc-700">Sent. {n(send.data.notified)} heads-ups went out.</p> : null}
        </div>
      ) : null}
    </CardBody></Card>
  );
}

function Bench({ b, onOpen }: { b: HqBoard; onOpen: (id: string) => void }) {
  return (
    <Card data-testid="hq-bench"><CardBody>
      <h2 className="font-semibold">Bench · {n(b.bench.length)} more themes</h2>
      <p className="text-sm text-zinc-500">Each comes back when it climbs. Nothing is thrown away.</p>
      <ul className="mt-2 divide-y divide-zinc-100">
        {b.bench.map(t => (
          <li key={t.id} className="flex flex-col gap-1 py-2 md:flex-row md:items-baseline md:justify-between" data-testid={`hq-bench-${t.id}`}>
            <div className="min-w-0">
              <button type="button" className="text-left text-sm font-medium hover:underline" onClick={() => onOpen(t.id)}>{t.title}</button>
              {t.sample ? <span className="ml-2 rounded bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-500">Sample</span> : null}
              <div className="text-xs text-zinc-500">{t.why}</div>
            </div>
            <div className="shrink-0 text-xs text-zinc-500">
              {t.skipped ? <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-800">{t.skipped}</span> : null}{" "}
              asked {n(t.asked)}
            </div>
          </li>
        ))}
      </ul>
      {!b.bench.length ? <p className="py-2 text-sm text-zinc-500">The bench is empty.</p> : null}
    </CardBody></Card>
  );
}

function LastSent({ b, onOpen }: { b: HqBoard; onOpen: (id: string) => void }) {
  const w = b.last_sent!;
  return (
    <Card><CardBody>
      <h2 className="font-semibold">Out now · {weekWords(w.week_of)}</h2>
      <ol className="mt-2 list-decimal pl-5 text-sm">
        {w.picks.map(p => (
          <li key={p.position}>
            <button type="button" className="text-left hover:underline" onClick={() => onOpen(p.theme.id)}>{p.theme.title}</button>
            <span className="text-zinc-500"> · {STATUS_LABEL[p.theme.status as Status]}</span>
          </li>
        ))}
      </ol>
    </CardBody></Card>
  );
}

/** Who said what, word for word (HQ only), with the theme's status, fit and note. */
function Voices({ id, board, onClose, onChanged }: { id: string; board: Board; onClose: () => void; onChanged: () => void }) {
  const [role, setRole] = useState<Role | null>(null);
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["admin", "ideas", "voices", id, role],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/ideas/themes/{theme_id}/voices", {
        params: { path: { theme_id: id }, query: { role } },
      });
      if (error) throw new Error("failed to load");
      return data!;
    },
  });
  const v = query.data;
  return (
    <Card data-testid="hq-voices"><CardBody>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-semibold">Who said what{v ? `: ${v.title}` : ""}</h2>
          <p className="text-sm text-zinc-500">Word for word. Only Randy and Jeff see names; nobody in the app does.</p>
        </div>
        <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
      </div>
      <ThemeControls id={id} onChanged={() => { onChanged(); void qc.invalidateQueries({ queryKey: ["admin", "ideas", "voices", id] }); }} />
      <div role="tablist" className="mt-3 flex flex-wrap gap-1 text-sm">
        {roleFilters(board).map(([key, label]) => (
          <button key={label} type="button" role="tab" aria-selected={role === key} data-testid={`hq-role-${key ?? "all"}`}
            onClick={() => setRole(key)}
            className={`rounded-full border px-3 py-1 ${role === key ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 text-zinc-600"}`}>
            {label}
          </button>
        ))}
      </div>
      {v?.repeaters.length ? <p className="mt-2 text-sm text-zinc-600">{`Keeps raising it: ${v.repeaters.join(", ")}`}</p> : null}
      {query.isLoading ? <p className="mt-2 text-sm text-zinc-500">Loading…</p> : (
        <ul className="mt-2 divide-y divide-zinc-100">
          {(v?.voices ?? []).map((x, i) => (
            <li key={i} className="py-2 text-sm" data-testid="hq-voice">
              <div className="flex flex-wrap gap-x-2 text-xs text-zinc-500">
                <span className="font-medium text-zinc-900">{x.author_name}</span>
                <span>{ROLE_LABEL[x.role as Role]}</span>
                <span>· {KIND_LABEL[x.kind]}</span>
                <span>· {new Date(x.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</span>
              </div>
              <p className="mt-0.5 whitespace-pre-wrap">“{x.words}”</p>
              {x.posted && x.posted !== x.words ? <p className="text-xs text-zinc-500">Shown as: “{x.posted}”</p> : null}
              {x.detail ? <p className="text-xs text-zinc-500">{x.detail}</p> : null}
            </li>
          ))}
        </ul>
      )}
      {v && !v.voices.length ? <p className="mt-2 text-sm text-zinc-500">Nobody{role ? " in this group" : ""} yet.</p> : null}
    </CardBody></Card>
  );
}

function ThemeControls({ id, onChanged }: { id: string; onChanged: () => void }) {
  const [status, setStatus] = useState<Status | "">("");
  const [operation, setOperation] = useState("");
  const [alreadyLine, setAlreadyLine] = useState("");
  const [fit, setFit] = useState<string>("");
  const [note, setNote] = useState("");
  const save = useMutation({
    mutationFn: async () => {
      const body: components["schemas"]["HqThemeUpdate"] = {};
      if (status) body.status = status;
      if (status === "already_in_2oodles") body.operation_id = operation.trim() || null;
      if (status === "already_in_2oodles" && alreadyLine.trim()) body.already_line = alreadyLine.trim();
      if (fit !== "") body.way_fit = Number(fit);
      if (note.trim()) body.hq_note = note.trim();
      const { data, error } = await api.PATCH("/api/v1/admin/ideas/themes/{theme_id}", { params: { path: { theme_id: id } }, body });
      if (error) throw new Error(errorMessage(error, "Couldn’t save."));
      return data!;
    },
    onSuccess: () => { setStatus(""); setOperation(""); setAlreadyLine(""); setFit(""); setNote(""); onChanged(); },
  });
  const dirty = status !== "" || fit !== "" || note.trim() !== "";
  return (
    <div className="mt-3 flex flex-wrap items-end gap-2 rounded-md bg-zinc-50 p-3 text-sm" data-testid="hq-controls">
      <label className="flex flex-col gap-1">
        <span className="text-xs text-zinc-500">Status (tells everyone who asked)</span>
        <select aria-label="Status" value={status} onChange={e => setStatus(e.target.value as Status | "")} className="h-8 rounded-md border border-zinc-300 px-2">
          <option value="">Keep as is</option>
          {Object.entries(STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </label>
      {status === "already_in_2oodles" ? (
        <label className="flex flex-col gap-1">
          <span className="text-xs text-zinc-500">The operation that does it</span>
          <input aria-label="Operation" value={operation} onChange={e => setOperation(e.target.value)} placeholder="Operation id, like care.create_task"
            className="h-8 rounded-md border border-zinc-300 px-2" />
        </label>
      ) : null}
      {status === "already_in_2oodles" ? (
        <label className="flex min-w-[14rem] flex-1 flex-col gap-1">
          <span className="text-xs text-zinc-500">What people read (blank: 2oodles’ own line)</span>
          <input aria-label="Already line" value={alreadyLine} onChange={e => setAlreadyLine(e.target.value)} placeholder="One plain line about the feature"
            className="h-8 rounded-md border border-zinc-300 px-2" />
        </label>
      ) : null}
      <label className="flex flex-col gap-1">
        <span className="text-xs text-zinc-500">Fit with the 2oodles Way</span>
        <select aria-label="Fit" value={fit} onChange={e => setFit(e.target.value)} className="h-8 rounded-md border border-zinc-300 px-2">
          <option value="">Keep as is</option>
          {[-1, 0, 1, 2].map(k => <option key={k} value={k}>{FIT_LABEL[k]}</option>)}
        </select>
      </label>
      <label className="flex min-w-[12rem] flex-1 flex-col gap-1">
        <span className="text-xs text-zinc-500">HQ note (only HQ sees it)</span>
        <input aria-label="HQ note" value={note} onChange={e => setNote(e.target.value)} placeholder="A note for Randy and Jeff"
          className="h-8 rounded-md border border-zinc-300 px-2" />
      </label>
      <Button size="sm" disabled={!dirty || save.isPending} data-testid="hq-save" onClick={() => save.mutate()}>Save</Button>
      {save.isError ? <p role="alert" className="w-full text-sm text-red-700">{save.error.message}</p> : null}
    </div>
  );
}


// --- phase E1: closing the loop ---------------------------------------------------------------

/** Mon: keep or swap the 5 · Tue–Sat the Pack votes · Sun voting closes · Mon Jeff's items ·
 * +2 weeks "Did it nail it?". Today's step is lit. */
function Rhythm({ steps }: { steps: HqWorkItems["rhythm"] }) {
  return (
    <ol className="grid grid-cols-2 gap-1.5 text-sm md:grid-cols-5" data-testid="hq-rhythm" aria-label="The weekly rhythm">
      {steps.map((s, i) => (
        <li key={i} aria-current={s.now ? "step" : undefined}
          className={`rounded-md border px-3 py-2 ${s.now ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 text-zinc-700"}`}>
          <strong className="block">{s.day}</strong>
          <span className={`text-xs ${s.now ? "text-zinc-200" : "text-zinc-500"}`}>{s.line}</span>
        </li>
      ))}
    </ol>
  );
}

/** Save text as a file (Send to coding agent; Download for the tracker). No outside service. */
function download(filename: string, markdown: string) {
  const url = URL.createObjectURL(new Blob([markdown], { type: "text/markdown" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Ready to build, for Jeff: each winning idea drafted (who asked, votes, replies, the smallest
 * fix, what it touches, done when), with Send to tracker and Send to coding agent. */
function ReadyToBuild({ work, loading, onChanged }: { work: HqWorkItems | undefined; loading: boolean; onChanged: () => void }) {
  if (loading) return <p className="text-sm text-zinc-500">Loading…</p>;
  if (!work) return <p role="alert" className="text-sm text-red-700">Couldn’t load Ready to build.</p>;
  return (
    <div className="space-y-3" data-testid="hq-work">
      <p className="text-sm text-zinc-500">When a week’s vote closes, each winning idea lands here, drafted so nobody retypes anything.</p>
      {work.items.map(i => <WorkItem key={i.id} item={i} onChanged={onChanged} />)}
      {!work.items.length ? <Card><CardBody><p className="text-sm text-zinc-500">Nothing is waiting. The next items come when Sunday’s vote closes.</p></CardBody></Card> : null}
    </div>
  );
}

function WorkItem({ item, onChanged }: { item: HqWorkItem; onChanged: () => void }) {
  const [said, setSaid] = useState("");
  const [confirming, setConfirming] = useState(false);
  const exportAs = useMutation({
    mutationFn: async ({ as, then }: { as: "tracker" | "agent"; then: "copy" | "download" }) => {
      const { data, error } = await api.POST("/api/v1/admin/ideas/work/{item_id}/export", {
        params: { path: { item_id: item.id }, query: { as } },
      });
      if (error || !data) throw new Error(errorMessage(error, "Couldn’t export it."));
      if (then === "copy") await globalThis.navigator.clipboard.writeText(data.markdown);
      else download(data.filename, data.markdown);
      return then === "copy" ? "Copied as markdown. Paste it into your tracker." : `Saved ${data.filename}.`;
    },
    onSuccess: words => { setSaid(words); onChanged(); },
  });
  const done = useMutation({
    mutationFn: async () => {
      const { error } = await api.PATCH("/api/v1/admin/ideas/work/{item_id}", { params: { path: { item_id: item.id } }, body: { status: "done" } });
      if (error) throw new Error(errorMessage(error, "Couldn’t mark it."));
    },
    onSuccess: onChanged,
  });
  return (
    <Card data-testid={`hq-work-${item.id}`}><CardBody>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-semibold">{item.title}</h2>
        <span className="text-xs text-zinc-500">
          {WORK_KIND[item.kind]} · {item.board === "pro" ? "Pro board" : "Family board"}{item.week_of ? ` · ${weekWords(item.week_of)}` : ""}
          {item.sample ? " · Sample" : ""}{item.status === "exported" ? " · Exported" : ""}
        </span>
      </div>
      <dl className="mt-2 grid gap-1.5 text-sm">
        <div><dt className="inline font-medium">Goal: </dt><dd className="inline">{item.goal}</dd></div>
        <div><dt className="inline font-medium">Who asked: </dt><dd className="inline">{item.who_asked_line}{item.votes_line ? ` · ${item.votes_line}` : ""}</dd></div>
        {item.build_on.length ? <div><dt className="inline font-medium">Build on it: </dt><dd className="inline">{item.build_on.map(r => `${r.body} (+${n(r.plus)})`).join(" · ")}</dd></div> : null}
        {item.concerns.length ? <div><dt className="inline font-medium">Concern: </dt><dd className="inline">{item.concerns.map(r => `${r.body} (+${n(r.plus)})`).join(" · ")}</dd></div> : null}
        {item.reasons.length ? <div><dt className="inline font-medium">What people said: </dt><dd className="inline">{item.reasons.map(r => `“${r}”`).join(" · ")}</dd></div> : null}
        <div><dt className="inline font-medium">Smallest fix: </dt><dd className="inline">{item.smallest_fix}</dd></div>
        <div><dt className="inline font-medium">Touches: </dt><dd className="inline" data-testid={`hq-touches-${item.id}`}>{item.touches.length ? item.touches.map(t => `${t.screen_name} (${t.operation})`).join(" · ") : TOUCHES_TBD}</dd></div>
        <div><dt className="inline font-medium">Done when: </dt><dd className="inline">{item.done_when}</dd></div>
      </dl>
      <p className="mt-1 text-xs text-zinc-400">{item.drafted_by === "ai" ? "Drafted by 2oodles AI" : "Drafted by 2oodles (no AI key: the plain draft)"}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" data-testid={`hq-tracker-copy-${item.id}`} disabled={exportAs.isPending} onClick={() => exportAs.mutate({ as: "tracker", then: "copy" })}>Send to tracker: copy</Button>
        <Button size="sm" variant="ghost" data-testid={`hq-tracker-download-${item.id}`} disabled={exportAs.isPending} onClick={() => exportAs.mutate({ as: "tracker", then: "download" })}>Download .md</Button>
        <Button size="sm" variant="ghost" data-testid={`hq-agent-${item.id}`} disabled={exportAs.isPending} onClick={() => exportAs.mutate({ as: "agent", then: "download" })}>Send to coding agent</Button>
        {confirming ? (
          <span className="flex items-center gap-2 text-sm" data-testid={`hq-done-confirm-${item.id}`}>
            Built? It leaves this list.
            <Button size="sm" data-testid={`hq-done-yes-${item.id}`} disabled={done.isPending} onClick={() => done.mutate()}>Yes, built</Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>Not yet</Button>
          </span>
        ) : <Button size="sm" variant="ghost" data-testid={`hq-done-${item.id}`} onClick={() => setConfirming(true)}>Mark built</Button>}
      </div>
      {said ? <p role="status" className="mt-2 text-sm text-zinc-600">{said}</p> : null}
      {exportAs.isError || done.isError ? <p role="alert" className="mt-2 text-sm text-red-700">{(exportAs.error ?? done.error)?.message}</p> : null}
    </CardBody></Card>
  );
}

/** One board's or month's score: both numbers, the ±, and its tier (review fixes 5). */
function ScoreBar({ row }: { row: HqScoreRow }) {
  const pct = row.score?.nailed_pct ?? 0;
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm" data-testid="hq-score-row">
      <span className="w-32 shrink-0">{row.label}</span>
      <span className="h-2 min-w-[6rem] flex-1 overflow-hidden rounded-full bg-zinc-100"><span className="block h-full bg-zinc-900" style={{ width: `${pct}%` }} /></span>
      <span className="shrink-0 text-right text-zinc-600">{scoreRowWords(row.score)}</span>
    </div>
  );
}

/** The Nailed-it score: both numbers, the tier (our own bar), the margin, the industry average,
 * per board and per month, every shipped idea's result, and HQ's numbers in one place. */
function NailedScore() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["admin", "ideas", "score"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/ideas/score");
      if (error) throw new Error("failed to load");
      return data!;
    },
  });
  if (query.isLoading) return <p className="text-sm text-zinc-500">Loading…</p>;
  const s = query.data as HqScore | undefined;
  if (!s) return <p role="alert" className="text-sm text-red-700">Couldn’t load the Nailed-it score.</p>;
  const lit = tierIndex(s.overall?.nailed_pct, s.tiers);
  return (
    <div className="space-y-3" data-testid="hq-score">
      <Card><CardBody>
        {s.overall ? (
          <>
            <div className="flex flex-wrap items-baseline gap-3">
              <strong className="text-3xl">{`${s.overall.nailed_pct}%`}</strong>
              {s.overall.tier_label ? <span className="rounded-full bg-zinc-900 px-2.5 py-0.5 text-xs font-semibold text-white">{s.overall.tier_label}</span> : null}
              <span className="text-sm text-zinc-600">{`Nailed it · ${s.improvements_line} · ± ${s.overall.margin}%`}</span>
            </div>
            <div className="mt-2 flex flex-wrap items-baseline gap-3">
              <strong className="text-xl">{`${s.overall.top2_pct}%`}</strong>
              <span className="text-sm text-zinc-600">{`Nailed it or Okay · ${s.industry_line}`}</span>
            </div>
          </>
        ) : <p className="text-sm text-zinc-500">No score yet: it shows once enough people have answered “Did it nail it?” on shipped ideas.</p>}
        <div className="mt-3 flex flex-wrap gap-1.5 text-xs" data-testid="hq-tiers">
          {s.tiers.map((t, i) => <span key={t} className={`rounded-full border px-2.5 py-0.5 ${i === lit ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 text-zinc-600"}`}>{t}</span>)}
        </div>
        <p className="mt-2 text-sm text-zinc-600">{s.own_bar_line} {s.honest_line}</p>
        <div className="mt-3 space-y-1.5">{s.boards.map(r => <ScoreBar key={r.label} row={r} />)}</div>
        {s.months.length ? <div className="mt-3 space-y-1.5"><h3 className="text-sm font-medium">By month</h3>{s.months.map(r => <ScoreBar key={r.label} row={r} />)}</div> : null}
        <p className="mt-3 text-sm text-zinc-600"><strong>{n(s.another_pass)}</strong> back on the board for another pass</p>
      </CardBody></Card>
      <Card><CardBody>
        <h2 className="font-semibold">Every shipped idea</h2>
        <ul className="mt-2 divide-y divide-zinc-100 text-sm">
          {s.results.map(r => <li key={r.theme_id} className="py-1.5" data-testid="hq-result"><strong>{r.title}</strong> <span className="text-zinc-500">· {r.board === "pro" ? "Pro" : "Family"} · {r.line}</span></li>)}
        </ul>
        {!s.results.length ? <p className="py-2 text-sm text-zinc-500">Nothing has shipped yet.</p> : null}
      </CardBody></Card>
      <Health />
      <Settings settings={s.settings} onSaved={() => void qc.invalidateQueries({ queryKey: ["admin", "ideas"] })} />
    </div>
  );
}

/** HQ's numbers in one place: when Nailed it closes, the tiers (our own bar), the industry
 * average and its source, and when "Did it nail it?" goes out. */
function Settings({ settings, onSaved }: { settings: HqSettings; onSaved: () => void }) {
  const [draft, setDraft] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, number | string> = {};
      for (const [k, v] of Object.entries(draft)) body[k] = k === "industry_source" ? v.trim() : Number(v);
      const { data, error } = await api.PATCH("/api/v1/admin/ideas/settings", { body });
      if (error) throw new Error(errorMessage(error, "Couldn’t save."));
      return data!;
    },
    onSuccess: () => { setDraft({}); onSaved(); },
  });
  const value = (k: string) => draft[k] ?? String((settings as Record<string, unknown>)[k] ?? "");
  return (
    <Card data-testid="hq-settings"><CardBody>
      <h2 className="font-semibold">HQ’s numbers</h2>
      <p className="text-sm text-zinc-500">One place for every threshold. The tiers are our own bar, set tougher than standard satisfaction surveys.</p>
      <div className="mt-3 grid gap-2 md:grid-cols-4">
        {SETTING_FIELDS.map(([k, label, min, max]) => (
          <label key={k} className="flex flex-col gap-1 text-sm">
            <span className="text-xs text-zinc-500">{label}</span>
            <input aria-label={label} type="number" min={min} max={max} value={value(k)} data-testid={`hq-setting-${k}`}
              onChange={e => setDraft(d => ({ ...d, [k]: e.target.value }))} className="h-8 rounded-md border border-zinc-300 px-2" />
          </label>
        ))}
        <label className="flex flex-col gap-1 text-sm md:col-span-4">
          <span className="text-xs text-zinc-500">Where the industry average comes from</span>
          <input aria-label="Industry source" value={value("industry_source")} data-testid="hq-setting-industry_source"
            onChange={e => setDraft(d => ({ ...d, industry_source: e.target.value }))} placeholder="The source, in a few words"
            className="h-8 rounded-md border border-zinc-300 px-2" />
        </label>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button size="sm" data-testid="hq-settings-save" disabled={!Object.keys(draft).length || save.isPending} onClick={() => save.mutate()}>Save</Button>
        {save.isSuccess ? <span role="status" className="text-sm text-zinc-600">Saved.</span> : null}
      </div>
      {save.isError ? <p role="alert" className="mt-2 text-sm text-red-700">{save.error.message}</p> : null}
    </CardBody></Card>
  );
}

// --- phase E2: help without a help desk -------------------------------------------------------

/** Problems: merged reports, worst first. Never voted on; Mark fixed tells everyone who hit it. */
function Problems({ list, loading, onChanged }: { list: HqProblem[] | undefined; loading: boolean; onChanged: () => void }) {
  if (loading) return <p className="text-sm text-zinc-500">Loading…</p>;
  if (!list) return <p role="alert" className="text-sm text-red-700">Couldn’t load the problems.</p>;
  return (
    <div className="space-y-3" data-testid="hq-problems">
      <p className="text-sm text-zinc-500">Reports are merged by what they say. Each person hears once when it’s fixed, with “Did this fix it?”.</p>
      {list.map(p => <ProblemCard key={p.id} p={p} onChanged={onChanged} />)}
      {!list.length ? <Card><CardBody><p className="text-sm text-zinc-500">No problems reported. Nothing to fix.</p></CardBody></Card> : null}
    </div>
  );
}

function ProblemCard({ p, onChanged }: { p: HqProblem; onChanged: () => void }) {
  const [note, setNote] = useState("");
  const [fixing, setFixing] = useState(false);
  const fix = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST("/api/v1/admin/ideas/problems/{problem_id}/fix", { params: { path: { problem_id: p.id } }, body: { note: note.trim() } });
      if (error) throw new Error(errorMessage(error, "Couldn’t mark it fixed."));
    },
    onSuccess: () => { setFixing(false); setNote(""); onChanged(); },
  });
  return (
    <Card data-testid={`hq-problem-${p.id}`}><CardBody>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-semibold">{p.title}</h2>
        <span className="flex items-center gap-2 text-xs">
          <span className={`rounded-full px-2 py-0.5 font-semibold ${SEVERITY_CLASS[p.severity]}`}>{SEVERITY_LABEL[p.severity]}</span>
          <span className="text-zinc-500">{peopleWords(p.people, p.reports)}{p.screen ? ` · ${p.screen}` : ""}{p.sample ? " · Sample" : ""}</span>
        </span>
      </div>
      {p.status === "fixed" ? <p className="mt-1 text-sm text-emerald-700">{`Fixed: ${p.fixed_note ?? ""} · ${n(p.fixed_yes)} said it’s fixed, ${n(p.fixed_no)} said it isn’t`}</p>
        : p.reopened ? <p className="mt-1 text-sm text-amber-800">{`Reopened ${n(p.reopened)} time${p.reopened === 1 ? "" : "s"} · ${n(p.fixed_no)} said the fix didn’t work`}</p> : null}
      <ul className="mt-2 divide-y divide-zinc-100 text-sm">
        {p.latest.map(r => (
          <li key={r.feedback_id} className="py-1.5">
            <span className="font-medium">{r.author_name}</span> <span className="text-zinc-500">· {new Date(r.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</span>
            <p className="whitespace-pre-wrap">“{r.words}”</p>
            <p className="text-xs text-zinc-500">{[r.device, r.last_steps.length ? `Last steps: ${r.last_steps.join(" → ")}` : null].filter(Boolean).join(" · ")}</p>
          </li>
        ))}
      </ul>
      {p.status === "open" ? (fixing ? (
        <div className="mt-2 flex flex-wrap items-end gap-2 rounded-md bg-zinc-50 p-3 text-sm" data-testid={`hq-fix-form-${p.id}`}>
          <label className="flex min-w-[16rem] flex-1 flex-col gap-1">
            <span className="text-xs text-zinc-500">What’s fixed, in a few words (everyone who reported it reads this)</span>
            <input aria-label="What’s fixed" value={note} onChange={e => setNote(e.target.value)} placeholder="What works again"
              className="h-8 rounded-md border border-zinc-300 px-2" />
          </label>
          <Button size="sm" data-testid={`hq-fix-confirm-${p.id}`} disabled={!note.trim() || fix.isPending} onClick={() => fix.mutate()}>{`Tell ${n(p.people)} ${p.people === 1 ? "person" : "people"} it’s fixed`}</Button>
          <Button size="sm" variant="ghost" onClick={() => setFixing(false)}>Not yet</Button>
        </div>
      ) : <Button size="sm" className="mt-2" data-testid={`hq-fix-${p.id}`} onClick={() => setFixing(true)}>Mark fixed</Button>) : null}
      {fix.isError ? <p role="alert" className="mt-2 text-sm text-red-700">{fix.error.message}</p> : null}
    </CardBody></Card>
  );
}

/** Already in 2oodles: what people ask for that exists, and the "Make it easier to find" rule. */
function Already({ data, loading, onChanged }: { data: HqAlready | undefined; loading: boolean; onChanged: () => void }) {
  const mark = useMutation({
    mutationFn: async (m: HqAlready["matches"][number]) => {
      for (const id of m.theme_ids) {
        const { error } = await api.PATCH("/api/v1/admin/ideas/themes/{theme_id}", {
          params: { path: { theme_id: id } },
          body: { operation_id: m.operation_id, status: "already_in_2oodles", ...(m.suggested_line ? { already_line: m.suggested_line } : {}), ...(m.values ? { operation_values: m.values } : {}) },
        });
        if (error) throw new Error(errorMessage(error, "Couldn’t mark it."));
      }
    },
    onSuccess: onChanged,
  });
  if (loading) return <p className="text-sm text-zinc-500">Loading…</p>;
  if (!data) return <p role="alert" className="text-sm text-red-700">Couldn’t load Already in 2oodles.</p>;
  return (
    <div className="space-y-3" data-testid="hq-already">
      <Card><CardBody>
        <h2 className="font-semibold">Asked for, already in 2oodles</h2>
        <p className="text-sm text-zinc-500">{`People get Show me and Turn it on for me instead of “not planned”. At ${n(data.threshold)} people, Jeff gets “Make it easier to find” in Ready to build.`}</p>
        <ul className="mt-2 divide-y divide-zinc-100 text-sm">
          {data.themes.map(t => <li key={t.id} className="py-1.5" data-testid="hq-already-theme"><strong>{t.title}</strong> <span className="text-zinc-500">· {t.board === "pro" ? "Pro" : "Family"} · {t.line}</span></li>)}
        </ul>
        {!data.themes.length ? <p className="py-2 text-sm text-zinc-500">None yet.</p> : null}
      </CardBody></Card>
      <Card><CardBody>
        <h2 className="font-semibold">Matched to something 2oodles already does</h2>
        <p className="text-sm text-zinc-500">2oodles matched these suggestions to an existing feature. Mark them, and the people who asked see Show me and Turn it on for me.</p>
        <ul className="mt-2 divide-y divide-zinc-100 text-sm">
          {data.matches.map(m => (
            <li key={`${m.board}-${m.operation_id}-${m.topic ?? ""}`} className="flex flex-wrap items-center justify-between gap-2 py-1.5" data-testid="hq-already-match">
              <span><strong>{m.suggested_line ?? m.operation_id}</strong> <span className="text-zinc-500">· {m.board === "pro" ? "Pro" : "Family"} · {m.line}</span></span>
              <Button size="sm" variant="ghost" disabled={mark.isPending} onClick={() => mark.mutate(m)}>Mark as Already in 2oodles</Button>
            </li>
          ))}
        </ul>
        {!data.matches.length ? <p className="py-2 text-sm text-zinc-500">None waiting.</p> : null}
        {mark.isError ? <p role="alert" className="text-sm text-red-700">{mark.error.message}</p> : null}
      </CardBody></Card>
    </div>
  );
}

/** Health: Heard to Shipped, contacts per 1,000 users, How do I answered, another pass, stuck
 * screens, Already in 2oodles; stuck screens and unanswered questions can go on a board. */
function Health() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["admin", "ideas", "health"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/ideas/health");
      if (error) throw new Error("failed to load");
      return data!;
    },
  });
  const put = useMutation({
    mutationFn: async ({ title, board }: { title: string; board: Board }) => {
      const { error } = await api.POST("/api/v1/admin/ideas/from-signal", { body: { board, title } });
      if (error) throw new Error(errorMessage(error, "Couldn’t add it."));
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "ideas"] }),
  });
  const h = query.data as HqHealth | undefined;
  if (!h) return null;
  return (
    <Card data-testid="hq-health"><CardBody>
      <h2 className="font-semibold">Health</h2>
      <div className="mt-2 grid gap-2 md:grid-cols-3">
        {h.numbers.map(x => <div key={x.line} className="rounded-md bg-zinc-50 p-3"><strong className="block text-xl">{x.value}</strong><span className="text-xs text-zinc-600">{x.line}</span></div>)}
      </div>
      {h.stuck.length ? <><h3 className="mt-3 text-sm font-medium">Where people get stuck this week</h3>
        <ul className="divide-y divide-zinc-100 text-sm">{h.stuck.map(x => <li key={x.screen} className="flex flex-wrap items-center justify-between gap-2 py-1.5" data-testid="hq-stuck">
          <span><strong>{x.screen}</strong> <span className="text-zinc-500">· {`${n(x.people)} people ${x.kinds}`}</span></span>
          <ToBoard disabled={put.isPending} onPut={board => put.mutate({ title: `Make ${x.screen} easier`, board })} />
        </li>)}</ul></> : null}
      {h.questions.length ? <><h3 className="mt-3 text-sm font-medium">“How do I…” questions Ask couldn’t answer</h3>
        <ul className="divide-y divide-zinc-100 text-sm">{h.questions.map(q => <li key={q.words} className="flex flex-wrap items-center justify-between gap-2 py-1.5" data-testid="hq-question">
          <span>“{q.words}” <span className="text-zinc-500">· {`${n(q.times)}×`}</span></span>
          <ToBoard disabled={put.isPending} onPut={board => put.mutate({ title: q.words.slice(0, 120), board })} />
        </li>)}</ul></> : null}
      {put.isError ? <p role="alert" className="text-sm text-red-700">{put.error.message}</p> : null}
    </CardBody></Card>
  );
}

function ToBoard({ disabled, onPut }: { disabled: boolean; onPut: (board: Board) => void }) {
  return <span className="flex gap-1">
    <Button size="sm" variant="ghost" disabled={disabled} onClick={() => onPut("family")}>To the Family board</Button>
    <Button size="sm" variant="ghost" disabled={disabled} onClick={() => onPut("pro")}>To the Pro board</Button>
  </span>;
}
